<template>
  <BaseField
    v-slot="{ id }"
    :label="label"
    hide-label
  >
    <VTextField
      :id="id"
      ref="inputRef"
      v-model="model"
      :placeholder="placeholder"
      prepend-inner-icon="search"
      v-bind="attrsWithoutClass"
    >
      <template #append-inner>
        <VBtnIcon
          v-if="model"
          icon="close"
          :aria-label="clearAriaLabel?.trim() || t('clearSearch')"
          data-testid="buttonClearSearch"
          variant="text"
          size="small"
          class="clear-search-btn"
          @click="onClear"
        />
      </template>
    </VTextField>
  </BaseField>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseField } from '@teamworksdev/vuetify';
import { omit } from 'lodash-es';
import { VTextField } from 'vuetify/components';

defineProps<{
  label: string;
  placeholder?: string;
  clearAriaLabel?: string;
}>();

const model = defineModel<string | null>();
const { t } = useI18n();
const inputRef = ref<InstanceType<typeof VTextField> | null>(null);

// `class` already falls through to the component's root element by default;
// forwarding it to VTextField as well would apply it twice.
const attrs = useAttrs();
const attrsWithoutClass = computed(() => omit(attrs, 'class', 'data-testid'));

const onClear = () => {
  model.value = null;
  inputRef.value?.focus();
};
</script>

<style lang="scss" scoped>
// Pin the clear-search button to the same 24px footprint as the bare VIcon it
// replaced, so the input height and placeholder alignment are unchanged. The
// doubled selector raises specificity to beat Vuetify's default button sizing
// without !important. It stays a real, keyboard-focusable button.
.clear-search-btn.clear-search-btn {
  width: 24px;
  min-width: 24px;
  height: 24px;
  --v-btn-height: 24px;

  // Flat at rest/hover like a plain icon; keep Vuetify's overlay for keyboard focus.
  &:not(:focus-visible) :deep(.v-btn__overlay) {
    opacity: 0;
  }
}
</style>
